export default (filterType, filterLabel, useParent = true, scoreEdit = null, propertyMap = {}) => ({
    filterType: filterType,
    filterLabel: filterLabel,
    useParent: useParent,
    scoreEdit: scoreEdit,
    propertyMap: propertyMap,
    selectedFilter: null,
    selectedValues: [],
    filterOptions: [],
    searchQuery: '',
    loading: false,
    hasMorePages: false,
    currentPage: 1,
    showDropdown: false,
    searchTimeout: null,
    autoApplyTimeout: null,
    integerValue: null,

    // Score filter state
    scoreFilterQuestion: null,
    scoreFilterOperator: 'between',
    scoreFilterValue: null,
    scoreFilterMin: null,
    scoreFilterMax: null,
    scoreFilterOperators: [],

    init() {
        this.loadFilterConfig();
    },

    async loadFilterConfig() {
        try {
            // Get filter configuration from parent component (now grouped)
            const availableFilters = this.$wire.get('availableFilters') || [];

            // Flatten grouped filters to find the one we need
            let foundFilter = null;
            for (const group of availableFilters) {
                foundFilter = group.find(f => f.value === this.filterType);
                if (foundFilter) break;
            }

            this.selectedFilter = foundFilter;

            // Load current values BEFORE loading options
            await this.loadCurrentValues();

            if (this.selectedFilter && this.selectedFilter.requiresOptions) {
                await this.loadFilterOptions();
            }

            if (this.selectedFilter?.filterType === 'score_filter') {
                await this.loadScoreFilterOperators();
            }
        } catch (error) {
            console.error('Failed to load filter config:', error);
        }
    },

    async loadCurrentValues() {
        try {
            const currentValues = await this.$wire.call('getCurrentFilterValues', this.filterType);
            this.selectedValues = currentValues || [];

            // For integer filters, also set the integerValue
            if (this.selectedFilter?.filterType === 'integer' && this.selectedValues.length > 0) {
                this.integerValue = this.selectedValues[0];
            }
        } catch (error) {
            console.warn('Could not load current values:', error);
            this.selectedValues = [];
        }
    },

    async loadScoreFilterOperators() {
        try {
            this.scoreFilterOperators = await this.$wire.call('getScoreFilterOperators');
        } catch (error) {
            console.error('Failed to load score filter operators:', error);
            this.scoreFilterOperators = [];
        }
    },

    selectScoreQuestion(question) {
        this.scoreFilterQuestion = question;
    },

    // Whether the currently selected operator needs a min/max range rather than a
    // single value. Driven by the operator's `requiresRange` flag from the backend,
    // falling back to 'between' while the operator list is still loading.
    get scoreFilterRequiresRange() {
        const operator = this.scoreFilterOperators.find(op => op.value === this.scoreFilterOperator);

        return operator ? operator.requiresRange : this.scoreFilterOperator === 'between';
    },

    get scoreFilterRangeInvalid() {
        if (!this.scoreFilterRequiresRange) {
            return false;
        }

        const min = parseFloat(this.scoreFilterMin);
        const max = parseFloat(this.scoreFilterMax);

        return !isNaN(min) && !isNaN(max) && min > max;
    },

    get canApplyScoreFilter() {
        if (!this.scoreFilterQuestion) {
            return false;
        }

        if (this.scoreFilterRequiresRange) {
            return this.scoreFilterMin !== null && this.scoreFilterMax !== null &&
                   this.scoreFilterMin !== '' && this.scoreFilterMax !== '' &&
                   !this.scoreFilterRangeInvalid;
        }

        return this.scoreFilterValue !== null && this.scoreFilterValue !== '';
    },

    applyScoreFilter() {
        if (!this.canApplyScoreFilter) {
            return;
        }

        const scoreFilter = {
            question_id: this.scoreFilterQuestion.id,
            operator: this.scoreFilterOperator,
        };

        if (this.scoreFilterRequiresRange) {
            scoreFilter.min = parseFloat(this.scoreFilterMin);
            scoreFilter.max = parseFloat(this.scoreFilterMax);
        } else {
            scoreFilter.value = parseFloat(this.scoreFilterValue);
        }

        // Replace the edited condition in place, or add a new one
        const property = this.getFilterProperty(this.selectedFilter);
        // Staging always targets the component hosting this bar. Writing to the
        // dashboard instead would make Livewire re-commit every reactive widget.
        const target = this.$wire;

        const currentFilters = target.get(this.stagedFilterProperty('scoreFilters')) || [];
        const editingIndex = this.scoreEdit?.index;

        const updatedFilters = editingIndex !== null && editingIndex !== undefined
            ? currentFilters.map((filter, index) => index === editingIndex ? scoreFilter : filter)
            : [...currentFilters, scoreFilter];

        target.updateFilter(property, updatedFilters);
        this.closeDropdown();
    },

    removeScoreFilter(index) {
        const property = this.getFilterProperty(this.selectedFilter);
        // Staging always targets the component hosting this bar. Writing to the
        // dashboard instead would make Livewire re-commit every reactive widget.
        const target = this.$wire;

        const currentFilters = target.get(this.stagedFilterProperty('scoreFilters')) || [];
        const updatedFilters = currentFilters.filter((_, i) => i !== index);

        target.updateFilter(property, updatedFilters.length > 0 ? updatedFilters : null);

        if (updatedFilters.length === 0) {
            this.closeDropdown();
        }
    },

    async loadFilterOptions() {
        if (!this.selectedFilter?.requiresOptions) {
            this.filterOptions = [];
            return;
        }

        this.loading = true;
        this.currentPage = 1;
        this.hasMorePages = false;

        try {
            const response = await this.$wire.call('getFilterOptions', this.filterType, 1, this.searchQuery);
            this.filterOptions = response.data || [];
            this.hasMorePages = response.hasMorePages || false;
            this.currentPage = response.currentPage || 1;
        } catch (error) {
            console.error('Failed to load filter options:', error);
            this.filterOptions = [];
            this.hasMorePages = false;
        } finally {
            this.loading = false;
        }
    },

    async loadMoreOptions() {
        if (!this.selectedFilter?.requiresOptions || !this.hasMorePages || this.loading) {
            return;
        }

        this.loading = true;

        try {
            const nextPage = this.currentPage + 1;
            const response = await this.$wire.call('getFilterOptions', this.filterType, nextPage, this.searchQuery);

            // Append new options to existing ones
            this.filterOptions = [...this.filterOptions, ...(response.data || [])];
            this.hasMorePages = response.hasMorePages || false;
            this.currentPage = response.currentPage || nextPage;
        } catch (error) {
            console.error('Failed to load more filter options:', error);
        } finally {
            this.loading = false;
        }
    },

    get filteredOptions() {
        if (!this.searchQuery || !this.filterOptions.length) {
            return this.filterOptions;
        }

        return this.filterOptions.filter(option =>
            option.name && option.name.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
    },

    get canApply() {
        return this.selectedValues.length > 0;
    },

    openDropdown() {
        this.showDropdown = true;

        // Pre-populate the score filter editor with the condition being edited
        if (this.scoreEdit) {
            this.scoreFilterQuestion = { id: this.scoreEdit.question_id, name: this.scoreEdit.question_name };
            this.scoreFilterOperator = this.scoreEdit.operator;
            this.scoreFilterValue = this.scoreEdit.value ?? null;
            this.scoreFilterMin = this.scoreEdit.min ?? null;
            this.scoreFilterMax = this.scoreEdit.max ?? null;
        }

        // If we need to load options and haven't loaded them yet, show loading state
        if (this.selectedFilter?.requiresOptions && this.filterOptions.length === 0) {
            this.loading = true;
        }

        this.$nextTick(() => {
            if (this.selectedFilter?.isSearchable && this.$refs.searchInput) {
                this.$refs.searchInput.focus();
            }
        });
    },

    closeDropdown() {
        this.showDropdown = false;
        this.searchQuery = '';
        this.scoreFilterQuestion = null;
        this.scoreFilterOperator = 'between';
        this.scoreFilterValue = null;
        this.scoreFilterMin = null;
        this.scoreFilterMax = null;
    },

    filterSearchResults(query) {
        if (!this.selectedFilter?.isSearchable) {
            return;
        }

        // Debounce search
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
            this.performSearch(query);
        }, 300);
    },

    async performSearch(query) {
        if (!this.selectedFilter?.requiresOptions || !this.selectedFilter?.isSearchable) {
            return;
        }

        this.searchQuery = query;

        // Reset pagination and reload options with search
        this.currentPage = 1;
        this.hasMorePages = false;
        await this.loadFilterOptions();
    },

    toggleValue(value) {
        // For single-select filter types, set the value and auto-apply
        if (['boolean', 'date_preset', 'date', 'year_period'].includes(this.selectedFilter.filterType)) {
            this.selectedValues = [value];
            this.autoApplyFilter();
            return;
        }

        // For multiselect filters, toggle the value in the array
        if (this.selectedFilter.filterType === 'multiselect') {
            const index = this.selectedValues.indexOf(value);
            if (index > -1) {
                this.selectedValues.splice(index, 1);
            } else {
                this.selectedValues.push(value);
            }
            this.debouncedAutoApply();
            return;
        }

        // For text and other types, set the value directly
        this.selectedValues = [value];
        this.debouncedAutoApply();
    },

    isValueSelected(value) {
        return this.selectedValues.includes(value);
    },

    selectSingleValueAndApply(value) {
        // Toggle the value (add if not selected, remove if selected)
        const index = this.selectedValues.indexOf(value);
        if (index > -1) {
            this.selectedValues.splice(index, 1);
        } else {
            this.selectedValues.push(value);
        }

        // Apply immediately and close dropdown (true = close)
        this.autoApplyFilter(true);
    },

    async applyChanges() {
        if (!this.canApply) {
            return;
        }

        try {
            // Get the property name from the filter
            const property = this.getFilterProperty(this.selectedFilter);
            const processedValue = this.processFilterValue(this.selectedFilter, this.selectedValues);

            // Update filter on appropriate target
            // Staging always targets the component hosting this bar. Writing to the
        // dashboard instead would make Livewire re-commit every reactive widget.
        const target = this.$wire;
            await target.updateFilter(property, processedValue);

            this.closeDropdown();
        } catch (error) {
            console.error('Could not apply filter values:', error);
        }
    },

    autoApplyFilter(closeMenu = null) {
        // Get the property name from the filter
        const property = this.getFilterProperty(this.selectedFilter);
        const processedValue = this.processFilterValue(this.selectedFilter, this.selectedValues);

        // Update filter on appropriate target
        // Staging always targets the component hosting this bar. Writing to the
        // dashboard instead would make Livewire re-commit every reactive widget.
        const target = this.$wire;
        target.updateFilter(property, processedValue);

        // Close menu for single-select filters (boolean, date_preset, year_period) or when explicitly requested
        if (closeMenu === true || this.selectedFilter.filterType === 'boolean' || this.selectedFilter.filterType === 'date_preset' || this.selectedFilter.filterType === 'year_period') {
            this.closeDropdown();
        }
    },

    debouncedAutoApply() {
        clearTimeout(this.autoApplyTimeout);
        this.autoApplyTimeout = setTimeout(() => {
            if (this.selectedValues.length > 0) {
                this.autoApplyFilter();
            } else {
                // If no values selected, remove the filter
                const property = this.getFilterProperty(this.selectedFilter);
                // Staging always targets the component hosting this bar. Writing to the
        // dashboard instead would make Livewire re-commit every reactive widget.
        const target = this.$wire;
                target.updateFilter(property, null);
                this.closeDropdown();
            }
        }, 500); // 500ms debounce for multiselect
    },

    applyIntegerFilter() {
        if (!this.integerValue || this.integerValue < 1) {
            return;
        }

        this.selectedValues = [this.integerValue];
        const property = this.getFilterProperty(this.selectedFilter);
        // Staging always targets the component hosting this bar. Writing to the
        // dashboard instead would make Livewire re-commit every reactive widget.
        const target = this.$wire;
        target.updateFilter(property, this.integerValue);
        this.closeDropdown();
    },

    cancel() {
        this.closeDropdown();
        // Reset to current values
        this.loadCurrentValues();
    },

    // Path to read a not-yet-applied filter value off the target component.
    // Dashboards stage into `pendingFilters`; a comparison editor is already
    // working on a staged copy, so it reads its own `filters`.
    stagedFilterProperty(property) {
        return this.useParent ? `pendingFilters.${property}` : `filters.${property}`;
    },

    getFilterProperty(filter) {
        if (!filter) return null;

        // Prefer the authoritative property map provided by the server
        // (VantageFilters::asProperties()) so this never drifts out of sync.
        if (this.propertyMap && this.propertyMap[filter.value]) {
            return this.propertyMap[filter.value];
        }

        // Fallback map used only if the server map wasn't supplied.
        const propertyMap = {
            'accounts': 'accounts',
            'units': 'units',
            'surveys': 'surveys',
            'roles': 'roles',
            'impact': 'impacts',
            'seniority': 'seniorities',
            'geography': 'geographies',
            'sector': 'sectors',
            'country': 'countries',
            'region': 'regions',
            'sub_region': 'subRegions',
            'remit': 'remits',
            'language': 'languages',
            'networks': 'networks',
            'sub_networks': 'subNetworks',
            'account_division': 'accountDivisions',
            'account_subdivision': 'accountSubDivisions',
            'account_brand': 'accountBrands',
            'date_period': 'datePeriod',
            'internal': 'internal',
            'gcl_coordinated': 'gclCoordinated',
            'opted_out': 'optedOut',
            'crc_survey': 'crcSurvey',
            'top_clients': 'topClients',
            'exclude_kantar': 'excludeKantar',
            'exclude_smollan': 'excludeSmollan',
            'exclude_russia': 'excludeRussia',
            'exclude_waves_in_progress': 'includeWavesInProgress',
            'gcl_clients': 'gclClients',
            'kcl_clients': 'kclClients',
            'critical_contact': 'criticalContact',
            't20_critical_contact': 't20CriticalContact',
            'score_filters': 'scoreFilters',
        };

        return propertyMap[filter.value] || filter.value;
    },

    processFilterValue(filter, values) {
        if (!filter || !values.length) {
            return null;
        }

        if (filter.filterType === 'boolean') {
            if (filter.invertsBooleanValue) {
                return !values[0];
            }

            return values[0];
        }

        if (filter.filterType === 'date_preset') {
            // Return the enum value for date presets
            return values[0];
        }

        if (filter.filterType === 'year_period') {
            // year_period is single-select and stored as a string (e.g. "123_h1")
            return values[0];
        }

        if (filter.filterType === 'multiselect') {
            return values;
        }

        if (filter.filterType === 'integer') {
            return parseInt(values[0], 10);
        }

        if (filter.filterType === 'score_filter') {
            return values;
        }

        if (filter.filterType === 'text' || filter.filterType === 'date') {
            return values[0];
        }

        return values;
    }
});
